import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { TuneOutlined, RestartAltOutlined } from "@mui/icons-material";
import { tokens, radius } from "../../theme/theme";
import { HEATING_OPTIONS } from "../siteConfig";
import { itemPriceInBase } from "../utils/format";

export const EMPTY_FILTERS = {
  q: "",
  rooms: "",
  region: "",
  heating: "",
  priceFrom: "",
  priceTo: "",
  furniture: false,
  technology: false,
  sort: "new",
};

export const SORT_OPTIONS = [
  { value: "new", label: "Спочатку нові" },
  { value: "priceAsc", label: "Дешевші спочатку" },
  { value: "priceDesc", label: "Дорожчі спочатку" },
  { value: "roomsAsc", label: "Менше кімнат" },
  { value: "roomsDesc", label: "Більше кімнат" },
];

const ROOM_CHOICES = ["1", "2", "3", "4+"];

export default function FilterPanel({ filters, onChange, districts, resultCount }) {
  const set = (patch) => onChange(Object.assign({}, filters, patch));

  const activeCount = [
    filters.q,
    filters.rooms,
    filters.region,
    filters.heating,
    filters.priceFrom,
    filters.priceTo,
    filters.furniture ? "1" : "",
    filters.technology ? "1" : "",
  ].filter(Boolean).length;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: radius.lg,
        position: { md: "sticky" },
        top: { md: 96 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <TuneOutlined sx={{ fontSize: 20, color: tokens.blue600 }} />
        <Typography variant="h6" sx={{ mr: "auto" }}>
          Фільтри
        </Typography>
        {activeCount > 0 && (
          <Chip size="small" color="primary" label={activeCount} />
        )}
      </Stack>

      <Stack spacing={2.2}>
        <TextField
          size="small"
          fullWidth
          label="Вулиця або опис"
          placeholder="Наприклад, Городоцька"
          value={filters.q}
          onChange={(e) => set({ q: e.target.value })}
        />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Кількість кімнат
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {ROOM_CHOICES.map((choice) => {
              const active = filters.rooms === choice;
              return (
                <Chip
                  key={choice}
                  label={choice}
                  clickable
                  onClick={() => set({ rooms: active ? "" : choice })}
                  variant={active ? "filled" : "outlined"}
                  color={active ? "primary" : "default"}
                  sx={{ minWidth: 48, fontWeight: 700 }}
                />
              );
            })}
          </Stack>
        </Box>

        <Autocomplete
          size="small"
          options={districts || []}
          value={filters.region || null}
          onChange={(e, value) => set({ region: value || "" })}
          renderInput={(params) => <TextField {...params} label="Район" />}
          noOptionsText="Нічого не знайдено"
        />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.4 }}>
            Ціна, грн / місяць
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
            Ціни в доларах враховуються за курсом
          </Typography>
          <Stack direction="row" spacing={1.2}>
            <TextField
              size="small"
              fullWidth
              type="number"
              label="від"
              value={filters.priceFrom}
              onChange={(e) => set({ priceFrom: e.target.value })}
            />
            <TextField
              size="small"
              fullWidth
              type="number"
              label="до"
              value={filters.priceTo}
              onChange={(e) => set({ priceTo: e.target.value })}
            />
          </Stack>
        </Box>

        <TextField
          size="small"
          select
          fullWidth
          label="Опалення"
          value={filters.heating}
          onChange={(e) => set({ heating: e.target.value })}
        >
          <MenuItem value="">Будь-яке</MenuItem>
          {HEATING_OPTIONS.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>

        <Divider />

        <Stack spacing={0.5}>
          <FormControlLabel
            control={
              <Switch
                checked={!!filters.furniture}
                onChange={(e) => set({ furniture: e.target.checked })}
              />
            }
            label={<Typography variant="body2">З меблями</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!filters.technology}
                onChange={(e) => set({ technology: e.target.checked })}
              />
            }
            label={<Typography variant="body2">З технікою</Typography>}
          />
        </Stack>

        <Divider />

        <TextField
          size="small"
          select
          fullWidth
          label="Сортування"
          value={filters.sort}
          onChange={(e) => set({ sort: e.target.value })}
        >
          {SORT_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="caption">
            Знайдено: <strong>{resultCount}</strong>
          </Typography>
          <Button
            size="small"
            variant="text"
            startIcon={<RestartAltOutlined />}
            onClick={() => onChange(Object.assign({}, EMPTY_FILTERS))}
            disabled={activeCount === 0}
          >
            Скинути
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Shared filtering + sorting so the home page and the catalog agree.
 *
 * Prices in the database are mixed UAH and USD, so every comparison —
 * the price range and the price sorts — runs on the hryvnia equivalent
 * (`itemPriceInBase`). Cards still display each listing's own currency.
 */
export function applyFilters(items, filters) {
  const f = filters || EMPTY_FILTERS;
  const q = (f.q || "").trim().toLowerCase();
  const priceFrom = f.priceFrom === "" ? null : Number(f.priceFrom);
  const priceTo = f.priceTo === "" ? null : Number(f.priceTo);

  let result = (items || []).filter((item) => {
    if (q) {
      const haystack = [item.street, item.region, item.info, item.state]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (haystack.indexOf(q) === -1) return false;
    }
    if (f.rooms) {
      const rooms = Number(item.rooms) || 0;
      if (f.rooms === "4+") {
        if (rooms < 4) return false;
      } else if (rooms !== Number(f.rooms)) {
        return false;
      }
    }
    if (f.region && item.region !== f.region) return false;
    if (f.heating && item.heating !== f.heating) return false;

    if (priceFrom !== null || priceTo !== null) {
      const price = itemPriceInBase(item);
      // Listings without a usable price stay out of an explicit price search.
      if (price === null) return false;
      if (priceFrom !== null && price < priceFrom) return false;
      if (priceTo !== null && price > priceTo) return false;
    }

    if (f.furniture && !item.furniture) return false;
    if (f.technology && !item.technology) return false;
    return true;
  });

  const byDate = (a, b) =>
    (b.dateEdit || b.dateFrom || 0) - (a.dateEdit || a.dateFrom || 0);

  // Unpriced listings sort last in BOTH directions rather than pretending to
  // be free — so the null check cannot simply be negated for descending.
  const byPrice = (descending) => (a, b) => {
    const pa = itemPriceInBase(a);
    const pb = itemPriceInBase(b);
    if (pa === null && pb === null) return 0;
    if (pa === null) return 1;
    if (pb === null) return -1;
    return descending ? pb - pa : pa - pb;
  };

  result = result.slice();
  if (f.sort === "priceAsc") result.sort(byPrice(false));
  else if (f.sort === "priceDesc") result.sort(byPrice(true));
  else if (f.sort === "roomsAsc") result.sort((a, b) => (a.rooms || 0) - (b.rooms || 0));
  else if (f.sort === "roomsDesc") result.sort((a, b) => (b.rooms || 0) - (a.rooms || 0));
  else result.sort(byDate);

  return result;
}
